import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import LivePriceTicker from './components/LivePriceTicker';
import NewsFeed from './components/NewsFeed';
import MarketAnalysis from './components/MarketAnalysis';
import RegulatoryUpdates from './components/RegulatoryUpdates';
import PriceCharts from './components/PriceCharts';
import PriceAlerts from './components/PriceAlerts';
import MarketSentiment from './components/MarketSentiment';
import { Helmet } from 'react-helmet';

const NewsAndMarketPrices = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [timeframe, setTimeframe] = useState('24h');
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const tabs = [
    { id: 'news', label: 'Market News', icon: 'Newspaper' },
    { id: 'prices', label: 'Live Prices', icon: 'TrendingUp' },
    { id: 'analysis', label: 'Expert Analysis', icon: 'BarChart3' },
    { id: 'regulatory', label: 'Regulations', icon: 'FileText' },
    { id: 'alerts', label: 'Price Alerts', icon: 'Bell' }
  ];

  const markets = [
    { id: 'all', label: 'All Markets' },
    { id: 'maharashtra', label: 'Maharashtra' },
    { id: 'uttar-pradesh', label: 'Uttar Pradesh' },
    { id: 'karnataka', label: 'Karnataka' },
    { id: 'tamil-nadu', label: 'Tamil Nadu' },
    { id: 'gujarat', label: 'Gujarat' }
  ];

  // Auto-refresh functionality
  useEffect(() => {
    if (isAutoRefresh) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isAutoRefresh]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'news':
        return <NewsFeed market={selectedMarket} timeframe={timeframe} />;
      case 'prices':
        return (
          <>
            <PriceCharts market={selectedMarket} timeframe={timeframe} />
            <MarketSentiment market={selectedMarket} />
          </>
        );
      case 'analysis':
        return <MarketAnalysis market={selectedMarket} />;
      case 'regulatory':
        return <RegulatoryUpdates market={selectedMarket} />;
      case 'alerts':
        return <PriceAlerts />;
      default:
        return <NewsFeed market={selectedMarket} timeframe={timeframe} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Sugar News & Market Prices | SugarHathi</title>
        <meta name="description" content="Live sugar market news, prices, analysis, and regulatory updates from SugarHathi." />
      </Helmet>
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} color="#FFFFFF" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">News & Market Prices</h1>
                  <p className="text-muted-foreground">
                    Real-time market intelligence, price trends, and industry updates
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <button
                  onClick={() => setIsAutoRefresh(!isAutoRefresh)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isAutoRefresh
                      ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-lg'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <Icon name="RefreshCw" size={16} className={isAutoRefresh ? 'animate-spin' : ''} />
                  <span className="whitespace-nowrap">{isAutoRefresh ? 'Auto Refresh ON' : 'Auto Refresh OFF'}</span>
                </button>
                
                <div className="text-sm text-muted-foreground whitespace-nowrap">
                  Last updated: {lastUpdate?.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>

          {/* Live Price Ticker */}
          <LivePriceTicker />

          {/* Controls Bar */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
                <label className="text-sm text-muted-foreground font-medium">Market:</label>
                <select
                  value={selectedMarket}
                  onChange={(e) => setSelectedMarket(e?.target?.value)}
                  className="flex-1 md:flex-initial px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00]"
                >
                  {markets?.map((market) => (
                    <option key={market?.id} value={market?.id}>
                      {market?.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full md:w-auto">
                <label className="text-sm text-muted-foreground font-medium whitespace-nowrap">Timeframe:</label>
                <div className="flex gap-2 flex-wrap">
                  {['1h', '24h', '7d', '30d']?.map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setTimeframe(tf)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        timeframe === tf
                          ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-lg'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {tf}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden md:block bg-card rounded-lg border border-border mb-6">
            <div className="flex items-center gap-2 p-2 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-lg'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Tabs - Dropdown Style */}
          <div className="md:hidden mb-6">
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e?.target?.value)}
                className="w-full px-4 py-3 bg-card border-2 border-[#ff8c00]/30 rounded-lg text-foreground font-medium appearance-none cursor-pointer focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ff8c00' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '3rem'
                }}
              >
                {tabs?.map((tab) => (
                  <option key={tab?.id} value={tab?.id}>
                    {tab?.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Icon 
                  name={tabs?.find(t => t?.id === activeTab)?.icon || 'TrendingUp'} 
                  size={20} 
                  className="text-[#ff8c00]"
                />
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewsAndMarketPrices;